import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  BarController,
  BarElement,
  LineController,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";

// Register chart.js components
ChartJS.register(
  BarController,
  LineController,
  BarElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const baseData = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  datasets: [
    {
      type: "line",
      label: "Stock Trend",
      data: [5000, 5500, 6000, 6000, 5800, 8000],
      borderColor: "#FFBD26",
      borderWidth: 5,
      pointRadius: 0,
      fill: false,
      tension: 0.6,
      order: 0,
      yAxisID: "y",
    },
    {
      type: "bar",
      label: "Expenses",
      data: [6000, 7000, 7400, 7400, 8300, 8000],
      backgroundColor: Array(6).fill("#9FAFAF"),
      borderColor: Array(6).fill("#000000"), // Black border color
      borderRadius: {
        topLeft: 10,
        topRight: 10,
        bottomLeft: 0,
        bottomRight: 0
      },
      borderSkipped: 'bottom', // This ensures the bottom border is visible
      order: 1,
      yAxisID: "y",
      barPercentage: 0.9,
      categoryPercentage: 0.8,
    },
    {
      type: "bar",
      label: "Revenue",
      data: [5000, 7000, 7800, 8200, 8700, 8000],
      backgroundColor: Array(6).fill("#CFD7D7"),
      borderColor: Array(6).fill("#000000"), // Black border color
      borderRadius: {
        topLeft: 10,
        topRight: 10,
        bottomLeft: 0,
        bottomRight: 0
      },
      borderSkipped: 'bottom', // This ensures the bottom border is visible
      order: 2,
      yAxisID: "y",
      barPercentage: 0.9,
      categoryPercentage: 0.9,
    },
  ],
};

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { mode: "index", intersect: false },
  },
  scales: {
    y: {
      min: 1000,
      max: 10000,
      grid: { display: false },
      position: "left",
      ticks: {
        stepSize: 1000,
        font: { size: 14, weight: "bold" },
            padding: 20,
        color: "#000",
        callback: (value) => (value === 0 ? "" : value),
      },
      offset: true,
    },
    yRight: {
      min: 1000,
      max: 10000,
      position: "right",
      grid: { drawOnChartArea: false },
      ticks: {
        stepSize: 1000,
        font: { size: 14, weight: "bold" },
        color: "#000",
        padding: 20, 
        callback: (value) => (value === 0 ? "" : value),
      },
      offset: true,
    },
    x: {
      grid: { display: false },
      ticks: {
        font: { size: 32, weight: "bold" },
        color: "#000",
        
      },
    },
  },
  layout: {
    padding: { top: 20, bottom: 40 },
  },
};

const ChartComponent = () => {
  const [hoveredColor, setHoveredColor] = useState(null);

  const filteredData = useMemo(() => {
    return {
      ...baseData,
      datasets: baseData.datasets.map((dataset) => {
        if (dataset.type === "line") return dataset;

        return {
          ...dataset,
          backgroundColor: dataset.backgroundColor.map((color) =>
            !hoveredColor || color === hoveredColor
              ? color
              : "rgba(0,0,0,0.05)"
          ),
        };
      }),
    };
  }, [hoveredColor]);

  return (
    <div className="relative md:mt-1 md:px-16 px-4  w-full h-[600px] mb-32 ">
 
      <div className="w-full h-[90%] z-10 ">
      
        <Chart type="bar" data={filteredData} options={options} />
      </div>

      {/* Color filter boxes */}
      <div className=" grid grid-cols-6 gap-1  md:pl-6 md:w-[150vh] ">
        {/* Expenses */}
        
        <div className="flex flex-col gap-2">
    
          <div className="flex items-center space-x-3">
            <div
              className="w-[34px] h-[34px] bg-[#FFBD26]  rounded-full cursor-pointer"
              onMouseEnter={() => setHoveredColor("#FFBD26")}
              onMouseLeave={() => setHoveredColor(null)}
              title="Highlighted Expense"
            />
            <h1 className="text-[14px] font-normal text-[#152C24]">Farmers</h1>
          </div>
        </div>

        {/* Revenue */}
        <div className="flex flex-col gap-2">
        
          <div className="flex items-center space-x-3">
            <div
              className="w-[34px] h-[34px] bg-[#2E453D] rounded-full cursor-pointer"
              onMouseEnter={() => setHoveredColor("#2E453D")}
              onMouseLeave={() => setHoveredColor(null)}
              title="Highlighted Revenue"
            />
            <h1 className="text-[14px]  font-normal text-[#2E453D]">Land </h1>
          </div>
        </div>
        <div className="flex flex-col gap-2">
    
          <div className="flex items-center space-x-3">
            <div
              className="w-[34px] h-[34px] bg-[#9FAFAF] rounded-full  cursor-pointer"
              onMouseEnter={() => setHoveredColor("#9FAFAF")}
              onMouseLeave={() => setHoveredColor(null)}
              title="Highlighted Expense"
            />
            <h1 className="text-[14px] font-normal text-[#152C24]">Production cost</h1>
          </div>
        </div>
        <div className="flex flex-col gap-2">
    
    <div className="flex items-center space-x-3">
      <div
        className="w-[34px] h-[34px] bg-[#CFD7D7] rounded-full  cursor-pointer"
        onMouseEnter={() => setHoveredColor("#CFD7D7")}
        onMouseLeave={() => setHoveredColor(null)}
        title="Highlighted Expense"
      />
      <h1 className="text-[14px]  font-normal text-[#152C24]">Revenue</h1>
    </div>
  </div>
  <div className="flex flex-col gap-2">
    
    <div className="flex items-center space-x-3">
      <div
        className="w-[34px] h-[34px] bg-[#152C24] rounded-full  cursor-pointer"
        onMouseEnter={() => setHoveredColor("#152C24")}
        onMouseLeave={() => setHoveredColor(null)}
        title="Highlighted Expense"
      />
      <h1 className="text-[14px]  font-normal text-[#152C24]">Inventory</h1>
    </div>
  </div>
  <div className="flex flex-col gap-2">
    
    <div className="flex items-center space-x-3">
      <div
        className="w-[34px] h-[34px] bg-[#F23939] rounded-full  cursor-pointer"
        onMouseEnter={() => setHoveredColor("#F23939")}
        onMouseLeave={() => setHoveredColor(null)}
        title="Highlighted Expense"
      />
      <h1 className="text-[14px]  font-normal text-[#152C24]">Price</h1>
    </div>
  </div>
      </div>
    </div>
  );
};

export default ChartComponent;